import { useEffect, useCallback, useMemo } from 'react'

import type {
  Journey,
  JourneyWithProgress,
  Level,
  Stage,
  CurrentsStorage
} from '@/features/journeys'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useLocalStorage } from '@/shared/hooks/useLocalStorage'
import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'
import { API_PATHS } from '@/shared/lib/data/paths'
import { sortByPosition } from '@/shared/lib/sort'
import type {
  JourneyProgress,
  StageProgress
} from '@/shared/types/user-progress'

import { calcJourneyRewards } from '../domain/calcJourneyRewards'
import { findPreviousLevel } from '../domain/findPreviousLevel'
import { sortJourneyStructure } from '../domain/sortJourneyStructure'

interface UseJourneysStoreProps {
  id?: number
  uid?: string
}

const calcPercent = (done: number, total: number) =>
  total > 0 ? Math.round((done / total) * 100) : 0

export const useJourneysStore = ({ id, uid }: UseJourneysStoreProps = {}) => {
  const {
    records: journeysRaw,
    isSuccess,
    isLoading,
    isError,
    refetch
  } = useOptimizedRecords<Journey>({
    path: API_PATHS.JOURNEYS
  })

  const { progress, journeyWasFinished, stageWasFinished, levelWasFinished } =
    useUserProgress()

  /* ------------------ Progress helpers ------------------ */

  const getStageProgress = useCallback(
    (
      stageId: number,
      {
        coin_reward = 0,
        point_reward = 0,
        levels
      }: { coin_reward?: number; point_reward?: number; levels: Level[] }
    ): StageProgress => {
      let acquiredCoins = 0
      let acquiredPoints = 0
      let finishedLevels = 0

      for (const level of levels) {
        if (levelWasFinished(level.id)) {
          finishedLevels++
          acquiredCoins += level.total_coin_reward ?? 0
          acquiredPoints += level.total_point_reward ?? 0
        }
      }

      const finished = stageWasFinished(stageId)

      if (finished) {
        acquiredCoins += coin_reward
        acquiredPoints += point_reward
      }

      return {
        finished,
        finishedPercent: calcPercent(finishedLevels, levels.length),
        aquiredCoins: acquiredCoins,
        aquiredPoints: acquiredPoints
      }
    },
    [levelWasFinished, stageWasFinished]
  )

  const getJourneyProgress = useCallback(
    (journey: Journey): JourneyProgress => {
      const finished = journeyWasFinished(journey.id)

      let blocked = false
      let blockedStatus: JourneyProgress['blockedStatus'] = 'never'
      let blockedByJourneyName: string | undefined

      if (journey.unlock_journey_id) {
        blocked = !journeyWasFinished(journey.unlock_journey_id)
        if (blocked) {
          blockedStatus = 'previousJourney'
          blockedByJourneyName =
            journey.unlock_journey?.name ??
            journeysRaw?.find(j => j.id === journey.unlock_journey_id)?.name
        }
      }

      const activeLevels = journey.active_level_ids ?? []
      let finishedCount = 0

      for (const stage of journey.active_stages ?? []) {
        for (const level of stage.active_levels ?? []) {
          if (levelWasFinished(level.id)) finishedCount++
        }
      }

      const rewards = calcJourneyRewards({
        journey,
        correctChallengeIds: new Set(progress?.correct_challenges ?? []),
        finishedChallengeIds: new Set(progress?.finished_challenges ?? []),
        finishedLevelIds: new Set(progress?.finished_levels ?? []),
        finishedStageIds: new Set(progress?.finished_stages ?? []),
        finishedJourneyIds: new Set(progress?.finished_journeys ?? [])
      })

      return {
        finished,
        blocked,
        blockedStatus,
        blockedByJourneyName,
        finishedPercent: calcPercent(finishedCount, activeLevels.length),
        rewards
      }
    },
    [journeyWasFinished, levelWasFinished, journeysRaw, progress]
  )

  /* ------------------ Journeys with progress ------------------ */

  const journeys = useMemo((): JourneyWithProgress[] => {
    return sortJourneyStructure(journeysRaw ?? []).map(journey => ({
      ...journey,
      _progress: getJourneyProgress(journey)
    }))
  }, [journeysRaw, getJourneyProgress])

  /* ------------------ Local storage ------------------ */

  const [currents, saveCurrents] = useLocalStorage<CurrentsStorage>(
    'pgee.currents',
    { stages: [], levels: [] }
  )

  /* ------------------ Compute currents ------------------ */

  const computedCurrents = useMemo(() => {
    if (!progress || journeys.length === 0) return null

    const currentStages: number[] = []
    const currentLevels: number[] = []

    for (const journey of journeys) {
      let currentStageId: number | null = null
      let currentLevelId: number | null = null

      const stages = [...(journey.active_stages ?? [])].sort(sortByPosition)

      for (const stage of stages) {
        const levels = [...(stage.active_levels ?? [])].sort(sortByPosition)

        for (const level of levels) {
          const levelFinished = levelWasFinished(level.id)
          if (!levelFinished && !currentLevelId) {
            currentStageId = stage.id
            currentLevelId = level.id
            break
          }
        }

        if (currentLevelId) break
      }

      if (currentStageId) currentStages.push(currentStageId)
      if (currentLevelId) currentLevels.push(currentLevelId)
    }

    return { stages: currentStages, levels: currentLevels }
  }, [journeys, progress, levelWasFinished])

  useEffect(() => {
    if (!computedCurrents) return
    saveCurrents(computedCurrents)
  }, [computedCurrents, saveCurrents])

  const currentStagesSet = useMemo(
    () => new Set(currents.stages),
    [currents.stages]
  )

  const currentLevelsSet = useMemo(
    () => new Set(currents.levels),
    [currents.levels]
  )

  const stageIsCurrent = useCallback(
    (stageId: number) => currentStagesSet.has(stageId),
    [currentStagesSet]
  )

  const levelIsCurrent = useCallback(
    (levelId: number) => currentLevelsSet.has(levelId),
    [currentLevelsSet]
  )

  /* ------------------ Helper functions ------------------ */

  const getLevelByUid = useCallback(
    (uid: string): { level: Level; stage: Stage; journey: Journey } | null => {
      for (const journey of journeys) {
        for (const stage of journey.active_stages ?? []) {
          const level = stage.active_levels?.find(l => l.uid === uid)
          if (level) {
            return { level, stage, journey }
          }
        }
      }
      return null
    },
    [journeys]
  )

  const getCurrentLevel = useCallback(
    (
      journeyId?: number
    ): { level: Level; stage: Stage; journey: Journey } | null => {
      if (!journeyId || currents.levels.length === 0) return null

      const currentLevelId = currents.levels.find(levelId => {
        for (const journey of journeys) {
          if (journey.id !== journeyId) continue
          for (const stage of journey.active_stages ?? []) {
            const level = stage.active_levels?.find(l => l.id === levelId)
            if (level) return true
          }
        }
        return false
      })

      if (!currentLevelId) return null

      for (const journey of journeys) {
        if (journey.id !== journeyId) continue
        for (const stage of journey.active_stages ?? []) {
          const level = stage.active_levels?.find(l => l.id === currentLevelId)
          if (level) {
            return { level, stage, journey }
          }
        }
      }

      return null
    },
    [journeys, currents.levels]
  )

  const getPreviousLevel = useCallback(
    (levelId: number): Level | null => findPreviousLevel(journeys, levelId),
    [journeys]
  )

  /* ------------------ Single journey selector ------------------ */

  const journey = useMemo(() => {
    if (!id && !uid) return null
    return journeys.find(j => j.id === id || j.uid === uid) ?? null
  }, [journeys, id, uid])

  return {
    journeys,
    journey,

    isSuccess,
    isLoading,
    isError,
    refetch,

    levelIsCurrent,
    levelWasFinished,
    stageIsCurrent,

    getJourneyProgress,
    getStageProgress,

    getCurrentLevel,
    getPreviousLevel,
    getLevelByUid
  }
}
